import { default as utils } from './utils.js';
import Mixins from './mixins.js';

const CLASSES = {
  LOADING: 'is-loading',
};

export default class Module {
  // static ------------------------------------------------------------------
  static init() {
    if (this._isInit) return;

    var self = this;
    var scroller = this.utils.fn.sync({
      throttle: true,
      onStart: function (a) {
        _trigger('scroll scroll-start', a);
      },
      onChange: function (a) {
        _trigger('scroll scroll-change', a);
      },
      onEnd: function (a) {
        _trigger('scroll scroll-end', a);
      },
    });
    var resizer = this.utils.fn.sync({
      throttle: true,
      onStart: function (a) {
        _trigger('resize resize-start', a);
      },
      onChange: function (a) {
        _trigger('resize resize-change', a);
      },
      onEnd: function (a) {
        _trigger('resize resize-end', a);
      },
    });

    function _trigger(name, e) {
      var items = self.items;
      var i = 0,
        l = items.length,
        item;

      for (; i < l; ++i) {
        item = items[i];
        item.trigger(name, e);
      }
    }

    utils.dom.events.add(window, 'scroll', {
      capture: true,
      passive: true,
      callback: function (e) {
        var target = e.originalTarget;
        var isWindow = utils.is.dom(target, true);
        var scrollTop = isWindow ? window.pageYOffset : target.scrollTop;
        var scrollLeft = isWindow ? window.pageXOffset : target.scrollLeft;

        if (target._scrollTop === scrollTop && target._scrollLeft === scrollLeft) return;

        var contentHeight = isWindow ? window.innerHeight : target.clientHeight;
        var contentWidth = isWindow ? window.innerWidth : target.clientWidth;

        var scrollBottom = scrollTop + contentHeight;
        var scrollRight = scrollLeft + contentWidth;
        var scrollWidth = isWindow ? document.documentElement.scrollWidth : target.scrollWidth;
        var scrollHeight = isWindow ? document.documentElement.scrollHeight : target.scrollHeight;

        scroller({
          scrollTarget: target,
          scrollTop: scrollTop,
          scrollBottom: scrollBottom,
          scrollLeft: scrollLeft,
          scrollRight: scrollRight,
          scrollWidth: scrollWidth,
          scrollHeight: scrollHeight,
          ratioX: scrollLeft / (scrollWidth - contentWidth),
          ratioY: scrollTop / (scrollHeight - contentHeight),
          scrollSizeChanged: scrollWidth !== target._scrollWidth || scrollHeight !== target._scrollHeight,
          originalEvent: e,
        });

        target._scrollWidth = scrollWidth;
        target._scrollHeight = scrollHeight;
        target._scrollTop = scrollTop;
        target._scrollLeft = scrollLeft;
      },
    });

    utils.dom.events.add(window, 'resize', function (e) {
      var width = window.innerWidth;
      var height = window.innerHeight;

      resizer({
        width: width,
        height: height,
      });
    });

    this._isInit = true;
  }

  static add(instance) {
    this.items = this.items || (this.items = []);
    this.items.push(instance);
    this.init();
  }

  static remove(instance) {
    if (!this.items) return;
    // @nicetohave we don't dynamically remove Module, so this isn't necessary
  }

  static getId(instance) {
    this.modules = this.modules || (this.modules = {});

    var name = instance.constructor.name;

    if (!this.modules[name]) {
      this.modules[name] = 0;
    }

    var id = name + '_' + this.modules[name];

    this.modules[name]++;

    return id;
  }

  // init --------------------------------------------------------------------
  constructor() {
    var self = this;
    var args = this.utils.toArray(arguments);

    this.id = Module.getId(this);
    this.children = {};
    this.data = { _isLoading: false, _isReady: false };

    var r = self._preInit.apply(self, args);
    if (r) {
      args = r;
    }

    // init
    if (utils.is.fn(this.preload)) {
      this.preload(_ready);
    } else {
      var response = _ready();
      if (response) return response;
    }

    function _ready() {
      var r = self.preInit.apply(self, args);
      if (r) {
        args = r;
      }

      var r = self.init.apply(self, args);
      if (r) return r;

      self.preAddEvents();
      self.addEvents();
      self.preRender();
      self.render();
      self.preMount();
      self.mount();

      self.isReady(true);

      // self.log('init');
    }

    Module.add(this);
  }

  _preInit() {}

  preInit() {}

  init() {}

  preAddEvents() {}

  addEvents() {}

  preRender() {}

  render() {}

  preMount() {}

  mount() {}

  preDestroy() {}

  destroy() {}

  // events ------------------------------------------------------------------

  // getter/setter -----------------------------------------------------------
  get $body() {
    return Module.$body || (Module.$body = this.$(document.body));
  }

  get $html() {
    return Module.$html || (Module.$html = this.$(document.documentElement));
  }

  get $document() {
    return Module.$document || (Module.$document = this.$(document));
  }

  get $window() {
    return Module.$window || (Module.$window = this.$(window));
  }

  // methods -----------------------------------------------------------------
  get(key, fallback) {
    if (key === undefined) {
      return this.data;
    } else if (key in this.data) {
      return this.data[key];
    } else {
      return fallback;
    }
  }

  set(key, value) {
    // set + defaults
    if (utils.is.obj(key, true)) {
      utils.extend(this.data, key);

      // set defaults
      if (utils.is.obj(value, true)) {
        var i;
        for (i in value) {
          if (i in this.data) continue;
          this.data[i] = value[i];
        }
      }
    } else if (utils.is(key)) {
      this.data[key] = value;
    }
    return this.data;
  }

  onReady(callback) {
    if (utils.is.fn(callback)) {
      if (this.isReady()) {
        callback.apply(this);
      } else {
        this.on('ready', callback);
      }
    }

    return this;
  }

  isReady(value) {
    if (value === true) {
      this.data._isReady = value;
      this.trigger('ready');
      return this;
    }
    return this.data._isReady;
  }

  isLoading(value, callback) {
    if (utils.is.bool(value)) {
      var $target = null; //this.$body;

      // have a specific query item to have the "loading" class
      if (utils.is.query(callback) || utils.is.el(callback)) {
        $target = this.$(callback);
        // use a function to have a complex loading behavior
      } else if (this.utils.is.fn(callback)) {
        callback.apply(this, [value]);
        $target = null;
        // have no classname changes
      } else if (callback === false) {
        $target = null;
      }

      // console.log('LOADING', this, value, $target);

      if ($target) {
        if (value) {
          $target.addClass(CLASSES.LOADING);
        } else {
          $target.removeClass(CLASSES.LOADING);
        }
      }

      this.data._isLoading = value;

      return this;
    }

    return this.data._isLoading;
  }

  isMobile() {
    return Tastet.is_mobile == 1;
  }

  scan($el, args) {
    var actions = Module.scanActions || {};

    args = args || {};
    args.keys = args.keys ? utils.toArray(args.keys) : false;

    for (var i in actions) {
      if (args.keys && !(i in args.keys)) continue;
      actions[i]($el, args);
    }
  }

  addScan(key, callback) {
    var actions = (Module.scanActions = Module.scanActions || {});
    actions[key] = callback;
  }
}

// Add mixins to the Class
Object.assign(Module, Mixins.Base);
Object.assign(Module.prototype, Mixins.Base, Mixins.Log, Mixins.Events, Mixins.States, Mixins.tracking);
